<template>
  <div>
    <Loading v-if="isLoading" />
    <div v-else>
      <!-- 兩面橫版走勢 -->
      <TrendExplanation
        :lottery-code="lotteryCode"
      />

      <div class="content-8__controlbar__item">
        <div class="c8-select">
          <div class="c8-select__title">
            <span class="c8-select__title__text" v-text="hmTitle"></span>
          </div>
          <div class="c8-select__content">
            <a
              v-for="hm in hmList"
              :key="`hm-selector-${hm.key}`"
              class="button-outline c8-select__content__item"
              :class="{ active: hm.key === currentHm }"
              href="javascript:;"
              @click="currentHm = hm.key"
            >
              <span class="button-outline__text" v-text="hm.label"></span>
            </a>
          </div>
        </div>
      </div>

      <div style="width: 100%; height: 50vh;">
        <v-chart ref="polar" :options="line" />
      </div>
    </div>
  </div>
</template>

<script>
import { hmList } from '@/utils/flyc-temp/lotteryLib'
import { getMultiTrend_web } from '@/api/desktop/trend'
import TrendExplanation from '@/components/web/pages/trend/TrendExplanation/index'
import Loading from '@/components/web/common/Loading'

// e-chart 的series 的自製建構子
const SeriesConstructor = function({ data = [], name } = {}) {
  SeriesConstructor.prototype.sequence++
  const sequence = SeriesConstructor.prototype.sequence
  if (name === undefined) {
    name = `name-${SeriesConstructor.prototype.sequence}`
  }

  return {
    name,
    sequence,
    type: 'line',
    label: {
      position: 'top',
      show: true,
      fontSize: 16,
      color: '#141414',
      formatter(data) {
        // 點上面要顯示的文字
        return data.value[2]
      }
    },
    symbolSize: 6,
    smooth: true,
    animation: false,
    data
  }
}
SeriesConstructor.prototype.sequence = 0

// textMapping
const liangMianHengBanLabelMapping = {
  'daxiao_da-danshuang_shuang': '大双',
  'daxiao_da-danshuang_dan': '大单',
  'daxiao_xiao-danshuang_shuang': '小双',
  'daxiao_xiao-danshuang_dan': '小单'
}

export default {
  name: 'WebLiangMianHengBanZouShihContent', // 兩面橫版走勢-內容
  components: {
    Loading,
    TrendExplanation
  },
  props: {
    lotteryCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 組出這個頁面會用到的api
      trendTypes: ['hmDaXiao', 'hmDanShuang'],

      form: {
        count: 40
      },

      currentHm: 'hm1',

      // 圖表
      line: {
        backgroundColor: '#fff',
        color: ['#37A2DA'],
        tooltip: { // hover 顯示的字樣
          trigger: 'axis',
          formatter(value) {
            return value.map((item) => {
              return `${item.value[3]}期 ${liangMianHengBanLabelMapping[item.value[1]]} ${item.value[2]}`
            }).join(', ')
          }
        },
        legend: {
          // 圖示
          show: false,
          left: 20,
          selected: {
            // 同時出現很多條線時的show/hide
            hm1: true,
            hm2: false,
            hm3: false,
            hm4: false,
            hm5: false,
            hm6: false,
            hm7: false,
            hm8: false,
            hm9: false,
            hm10: false
          }
        },
        yAxis: {
          type: 'category',
          nameRotate: 0,
          nameLocation: 'middle',
          name: '',
          axisLabel: {
            formatter(value) {
              // value-label 的轉置
              return liangMianHengBanLabelMapping[value]
            }
          },

          // 直接寫死，倒序
          data: [{
            value: 'daxiao_xiao-danshuang_dan'
          }, {
            value: 'daxiao_xiao-danshuang_shuang'
          }, {
            value: 'daxiao_da-danshuang_dan'
          }, {
            value: 'daxiao_da-danshuang_shuang'
          }],
          nameTextStyle: {
            rich: {
              x: {
                fontSize: 24,
                padding: 12,
                color: 'black',
                height: 4
              }
            }
          }
        },
        xAxis: {
          type: 'category',
          data: [],
          nameLocation: 'middle',
          name: '期号',
          axisLabel: {
            interval: 0,
            rotate: 0
          },
          nameTextStyle: {
            fontSize: 16,
            padding: [20, 0, 0, 0]
          }
        },
        series: []
      },

      isLoading: true,

      lotteryType: ''
    }
  },
  computed: {
    ...hmList,
    hmTitle() {
      return this.lotteryType === 'pk10' ? '筛选名次: ' : '筛选球位: '
    }
  },
  watch: {
    lotteryCode: {
      immediate: true,
      async handler(lotteryCode) {
        if (!lotteryCode) return

        this.lotteryType = await this.$getLotteryType(lotteryCode)

        if (this.lotteryType === 'pk10') {
          this.getTrendData(lotteryCode)
        }
      }
    },
    currentHm: {
      immediate: true,
      handler(currentHm, previousHm) {
        if (previousHm) {
          this.line.legend.selected[previousHm] = false
        }
        this.line.legend.selected[currentHm] = true
      }
    }
  },
  methods: {
    async getTrendData(lotteryCode) {
      // reset
      this.line.xAxis.data = []
      this.line.series = []

      this.isLoading = true

      const { trendTypes, form } = this

      // 兩面橫版走勢只有pk10 有，格式也和其他橫版走勢不同
      // 所以直接寫了special case 給他
      const [data, error] = await getMultiTrend_web(lotteryCode, trendTypes.join(','), form)
      if (error) return

      const hmMap = {}

      data.records.forEach((item) => {
        const { period: defaultPeriod, balls } = item
        const period = defaultPeriod.slice(-3)
        const fullPeriod = defaultPeriod

        // binding-chart-x-axis
        this.line.xAxis.data.push(period)

        const daxiaoList = item.dataList.hmDaXiao.hmDaXiao
        const danshuangList = item.dataList.hmDanShuang.hmDanShuang

        daxiaoList.forEach((daxiaoItem, index) => {
          const hmKey = `hm${index + 1}`
          const daxiaoValue = daxiaoList[index]
          const danshuangValue = danshuangList[index]
          const value = `${daxiaoValue}-${danshuangValue}`

          hmMap[hmKey] = hmMap[hmKey] ? hmMap[hmKey] : []

          hmMap[hmKey].push({ value, index, ballNumber: balls[index], balls, period, fullPeriod })
        })
        item.dataList.hmDanShuang
      })

      Object.keys(hmMap).forEach((hmKey) => {
        const seriesData = hmMap[hmKey].reduce((array, item) => {
          const { period, fullPeriod, value, ballNumber } = item

          // 這裡可以客製化一些事情
          array.push({ value: [period, value, ballNumber, fullPeriod], label: {}})

          return array
        }, [])
        const name = hmKey

        // binding-chart-series-data
        this.line.series.push(new SeriesConstructor({ name, data: seriesData }))
      })

      this.$nextTick(() => { this.isLoading = false })
    }
  }
}
</script>
